import _ from 'underscore';
import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { AssetEventRecordType, AssetType, POST_ASSET_EVENT_NAME } from '@bingads-webui-campaign-react/copilot-common';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { ImageSuggestionCarousel } from '@bingads-webui-campaign-react/image-suggestion-carousel';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { MessageBar, MessageBarType } from '@bingads-webui-react/fabric-control';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';

import { ImageRecommendationsPanel } from './image-recommendations-panel';
import { getImageSuggestionCarouselStyles, getCarouselStyles, getFullyShownImagesCount } from './image-suggestion-carousel-styles';

export const SuggestionViewImageCount = 5;
const MaxSuggestionsInCarousel = 15;
const isEmptySuggestionsHintEnabled = true;

const PmaxImageSuggestionCarouselInternal = (props) => {
  const {
    i18n,
    addImageCallback = _.noop,
    maxUploadNum,
  } = props;

  const [recommendationContext, , reportRecommendations] = useContext(RecommendationContext);
  const {
    themes, imageAssets, postAssetEventLog = _.noop, copilotTracking,
  } = recommendationContext;
  const { carouselState, panelState } = useMemo(() => ({
    carouselState: {
      carousel: {
        isError: _.isEmpty(imageAssets),
      },
      suggestion: {
        images: imageAssets,
      },
    },
    panelState: {
      panelImages: imageAssets,
      hasMore: false,
    },
  }), [imageAssets]);
  const { suggestion, carousel } = carouselState;
  const { images: suggestionImages, inProgress: suggestionInProgress } = suggestion;

  const [imagesToSuggest, setImagesToSuggest] = useState([]);
  const [carouselErrorMessage, setCarouselErrorMessage] = useState({});
  const [blockCarousel, setBlockCarousel] = useState(false);
  const [isSuggestionsPanelOpen, setIsSuggestionsPanelOpen] = useState(false);

  const logCopilotViewedImageSuggestions = useCallback((suggestionIds) => {
    if (copilotTracking) {
      const viewedImageSuggestionIds = copilotTracking.getUntrackedViewedSuggestionIds(suggestionIds);
      if (viewedImageSuggestionIds.length > 0) {
        postAssetEventLog({
          EventName: POST_ASSET_EVENT_NAME,
          EventValue: {
            AssetType: _.map(viewedImageSuggestionIds, () => AssetType.IMAGE),
            SuggestionIds: viewedImageSuggestionIds,
            RecordType: AssetEventRecordType.VIEWED,
          },
        });
        copilotTracking.trackViewedSuggestionIds(viewedImageSuggestionIds);
      }
    }
  }, [copilotTracking, postAssetEventLog]);

  if (isSuggestionsPanelOpen) {
    const panelViewedSuggestionIds = _.pluck(panelState.panelImages, 'suggestionId');
    reportRecommendations({ view: panelViewedSuggestionIds });
    logCopilotViewedImageSuggestions(panelViewedSuggestionIds);
  }

  useEffect(() => {
    const truncatedImages = _.isArray(suggestionImages) && suggestionImages.slice(0, MaxSuggestionsInCarousel);
    setImagesToSuggest(truncatedImages || []);
  }, [suggestionImages]);

  const classes = useDefaultStyles(getImageSuggestionCarouselStyles);

  const addImages = useCallback((addedImages) => {
    setBlockCarousel(true);
    addImageCallback(addedImages, constants.imageOptionKeys.Image)
      .then((response) => {
        if (response.processedImages || response.processedImage) {
          const processedImages = response.processedImages || [response.processedImage];

          const suggestionIds = _.map(
            processedImages,
            image => image.suggestionId
          );
          logCopilotViewedImageSuggestions(suggestionIds);

          _.forEach(
            processedImages,
            (image) => {
              copilotTracking.trackSelectedImageAssets({
                ImageAssets: [{ suggestionId: image.suggestionId, imageId: image.idString }],
              });
            }
          );
        }
        if (response.errorMessage) {
          setCarouselErrorMessage({
            type: MessageBarType.error,
            message: response.errorMessage,
          });
        }
        return Promise.resolve();
      })
      .finally(() => {
        const filteredImagesToSuggest = _.reject(
          imagesToSuggest,
          image => _.any(addedImages, addedImage => addedImage.fullUrl === image.fullUrl)
        );

        setImagesToSuggest(filteredImagesToSuggest);
        setBlockCarousel(false);
        setIsSuggestionsPanelOpen(false);
      });
  }, [addImageCallback, copilotTracking, imagesToSuggest, logCopilotViewedImageSuggestions]);

  const onAdd = useCallback(images => addImages(images), [addImages]);

  const onSelectImage = useCallback((fullUrl, _index, suggestionId) =>
    addImages([{ fullUrl, suggestionId }]), [addImages]);

  const onSelectSeeAllImages = useCallback(() => setIsSuggestionsPanelOpen(true), []);

  const onOffsetChange = useCallback((offset) => {
    const viewedImages = imagesToSuggest.slice(offset, offset + SuggestionViewImageCount);
    const suggestionIds = _.pluck(viewedImages, 'suggestionId');
    reportRecommendations({ view: suggestionIds });
    logCopilotViewedImageSuggestions(suggestionIds);
  }, [imagesToSuggest, logCopilotViewedImageSuggestions, reportRecommendations]);

  const shouldDisplayEmptySuggestionsHint =
    isEmptySuggestionsHintEnabled && !suggestionInProgress && maxUploadNum > 0 && _.isEmpty(imagesToSuggest);

  return (
    <React.Fragment>
      <div className={classes.ImageSuggestionCarouselContainer}>
        {shouldDisplayEmptySuggestionsHint && (
          <MessageBar
            className={classNames('empty-image-suggestions-hint', classes.InfoMessageBar)}
            isMultiline
            messageBarType={MessageBarType.info}
          >
            {i18n.getString(_TL_('Select the add image icon above to find relevant stock images or generate custom AI images for your ads.'))}
          </MessageBar>
        )}
        <UiBlocker
          blocking={blockCarousel}
          loadingText={i18n.getString(_TL_('Loading...'))}
        >
          <ImageSuggestionCarousel
            title={_TL_('Recommended images')}
            helpTopic="pop_BA_Recommendations_Pmax_Recommended_Images"
            isLoading={suggestionInProgress}
            images={maxUploadNum > 0 ? imagesToSuggest : []}
            i18n={i18n}
            isEmpty={carousel && carousel.isError}
            messageInfo={carouselErrorMessage}
            showEllipsisWhenNoMoreImages
            showViewAllInHeader
            stylingOverrides={{
              getCarouselStyles,
              getFullyShownImagesCount,
              initialImageContainerLeftMargin: 10,
              seeAllLinkText: _TL_('View all'),
            }}
            onSelectImage={onSelectImage}
            onSelectSeeAll={onSelectSeeAllImages}
            onOffsetChange={onOffsetChange}
          />
        </UiBlocker>
      </div>
      {isSuggestionsPanelOpen &&
        <ImageRecommendationsPanel
          i18n={i18n}
          title={_TL_('Recommended images')}
          images={panelState.panelImages}
          themes={themes}
          hasMore={panelState.hasMore}
          loadNextPage={panelState.getMoreImageSuggestions}
          maxUploadNum={maxUploadNum}
          onAdd={onAdd}
          onSelectImage={onSelectImage}
          onDismiss={() => setIsSuggestionsPanelOpen(false)}
        />
      }
    </React.Fragment>);
};

PmaxImageSuggestionCarouselInternal.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  instrumentation: PropTypes.shape({}).isRequired,
  addImageCallback: PropTypes.func.isRequired,
  maxUploadNum: PropTypes.number.isRequired,
};

export const PmaxImageSuggestionCarousel = withInstrumentation(PmaxImageSuggestionCarouselInternal);
